import { createSlice } from '@reduxjs/toolkit';

import { attachAssetsToTargets } from '../products/attachAssetsToTargets';
import { searchMediaLibraryModal } from '../mediaLibraryModal/search';
import { select as selectAssetsRange } from '../helpers/assets';

const getDefaultQuery = () => ({
  collectionIds: (typeof window !== 'undefined' && window.rootCollectionId) || undefined,
});

const syncSelectedAssetsById = (state) => {
  const nextMap = {};
  state.selectedIds.forEach((id) => {
    const existing = state.selectedAssetsById[id];
    if (existing) {
      nextMap[id] = existing;
      return;
    }
    const fromItems = state.items.find((item) => item._id === id);
    if (fromItems) {
      nextMap[id] = fromItems;
    }
  });
  state.selectedAssetsById = nextMap;
};

const refreshSelectedAssetsFromItems = (state) => {
  const selectedSet = new Set(state.selectedIds);
  state.items.forEach((item) => {
    if (item?._id && selectedSet.has(item._id)) {
      state.selectedAssetsById[item._id] = item;
    }
  });
};

export const initialState = {
  isOpen: false,
  attachContext: null,
  query: {},
  items: [],
  total: 0,
  isLoaded: true,
  selectedIds: [],
  selectedAssetsById: {},
  lastClickedId: null,
  inProgress: false,
  attaching: false,
};

const mediaLibraryModalSlice = createSlice({
  name: 'mediaLibraryModal',
  initialState,
  reducers: {
    openMediaLibraryModal(state, { payload }) {
      const attachContext = payload?.attachContext || null;
      const rootCollectionId = payload?.rootCollectionId
        || (typeof window !== 'undefined' && window.rootCollectionId)
        || null;

      state.isOpen = true;
      state.attachContext = attachContext;
      state.query = rootCollectionId ? { collectionIds: rootCollectionId } : getDefaultQuery();
      state.items = [];
      state.total = 0;
      state.isLoaded = false;
      state.selectedIds = [];
      state.selectedAssetsById = {};
      state.lastClickedId = null;
      state.inProgress = false;
      state.attaching = false;
    },
    closeMediaLibraryModal() {
      return initialState;
    },
    setMediaLibraryModalQuery(state, { payload }) {
      state.query = payload || {};
    },
    selectMediaLibraryModalAsset(state, { payload }) {
      const {
        id, value, isRange = false, attachedIds,
      } = payload || {};
      if (!id) return;

      const fallbackIds = state.attachContext?.draftMediaStrategy === 'replace'
        ? []
        : (state.attachContext?.attachedAssetIds || []);
      const attachedSet = new Set(
        (attachedIds !== undefined ? attachedIds : fallbackIds).map(String),
      );
      const isAttached = attachedSet.has(String(id));
      if (isAttached && !isRange) return;

      state.selectedIds = selectAssetsRange(
        value,
        id,
        isRange,
        state.lastClickedId,
        state.items,
        state.selectedIds,
      );
      if (attachedSet.size) {
        state.selectedIds = state.selectedIds.filter(
          (selectedId) => !attachedSet.has(String(selectedId)),
        );
      }
      if (!isAttached) {
        state.lastClickedId = id;
      }
      syncSelectedAssetsById(state);
    },
    deselectAllMediaLibraryModalAssets(state) {
      state.selectedIds = [];
      state.selectedAssetsById = {};
      state.lastClickedId = null;
    },
    removeMediaLibraryModalSelectedAsset(state, { payload: id }) {
      if (!id) return;
      state.selectedIds = state.selectedIds.filter((selectedId) => selectedId !== id);
      delete state.selectedAssetsById[id];
      if (state.lastClickedId === id) {
        state.lastClickedId = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchMediaLibraryModal.pending, (state, { meta }) => {
        state.inProgress = true;
        if (!meta.arg?.loadMore) {
          state.isLoaded = false;
        }
      })
      .addCase(searchMediaLibraryModal.fulfilled, (state, { payload }) => {
        state.inProgress = false;
        state.isLoaded = true;
        state.total = payload?.total || 0;
        state.items = payload?.append
          ? [...state.items, ...(payload.items || [])]
          : (payload?.items || []);

        if (!payload?.append) {
          const visibleIds = new Set(state.items.map((item) => item._id));
          if (state.lastClickedId && !visibleIds.has(state.lastClickedId)) {
            state.lastClickedId = null;
          }
        }

        refreshSelectedAssetsFromItems(state);
      })
      .addCase(searchMediaLibraryModal.rejected, (state) => {
        state.inProgress = false;
        state.isLoaded = true;
      })
      .addCase(attachAssetsToTargets.pending, (state) => {
        if (state.isOpen) state.attaching = true;
      })
      .addCase(attachAssetsToTargets.fulfilled, (state) => {
        state.attaching = false;
      })
      .addCase(attachAssetsToTargets.rejected, (state) => {
        state.attaching = false;
      });
  },
});

export const {
  openMediaLibraryModal,
  closeMediaLibraryModal,
  setMediaLibraryModalQuery,
  selectMediaLibraryModalAsset,
  deselectAllMediaLibraryModalAssets,
  removeMediaLibraryModalSelectedAsset,
} = mediaLibraryModalSlice.actions;

export default mediaLibraryModalSlice.reducer;
